/**
 * ProjectDetailPage.jsx - Dettaglio Progetto + Gestione Materiali
 * 100% Supabase-driven, zero mock data
 * 
 * Features:
 * - Visualizza info progetto
 * - Lista materiali del progetto
 * - Aggiungi materiale (modal con ricerca)
 * - Rimuovi materiale
 * - Note per ogni materiale
 * - DRAG & DROP per riordinare materiali
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit3,
  X,
  Search,
  Calendar,
  Box,
  Sparkles,
  MessageSquare,
  ExternalLink,
  Check,
  Loader2,
  GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import SortableMaterialsGrid from '../components/projects/SortableMaterialsGrid';

import {
  getProjectById,
  updateProject,
  getProjectMaterials,
  addMaterialToProject,
  removeMaterialFromProject,
  updateProjectMaterialNotes,
  reorderProjectMaterials,
  getMaterials
} from '../services/projectService';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [project, setProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(null);

  // Add Material Modal State
  const [searchQuery, setSearchQuery] = useState('');
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingMaterial, setAddingMaterial] = useState(false);

  // Edit Project State
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    client: '',
    cover_image: ''
  });
  const [saving, setSaving] = useState(false);

  // Notes State
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await getProjectById(projectId);
      
      if (fetchError) throw fetchError;
      if (!data) throw new Error('Progetto non trovato');

      setProject(data);
      setEditForm({
        name: data.name || '',
        description: data.description || '',
        client: data.client || '',
        cover_image: data.cover_image || ''
      });

      // Extract materials from project_materials join
      const projectMaterials = data.project_materials?.map(pm => ({
        ...pm,
        material: pm.materials || null
      })) || [];
      setMaterials(projectMaterials);

    } catch (err) {
      console.error('Fetch project error:', err);
      setError(err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId, fetchProject]);

  // ============================================================================
  // SEARCH MATERIALS
  // ============================================================================

  const searchMaterials = useCallback(async (query) => {
    setSearchLoading(true);
    try {
      const { data } = await getMaterials({ 
        search: query || '',
        limit: 20 
      });
      
      // Filter out materials already in project
      const existingIds = materials.map(m => m.material_id).filter(Boolean);
      const filtered = data.filter(m => !existingIds.includes(m.id));
      
      setAvailableMaterials(filtered);
    } catch (err) {
      console.error('Search materials error:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [materials]);

  // Debounced search
  useEffect(() => {
    if (showAddMaterial) {
      const timer = setTimeout(() => {
        searchMaterials(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, showAddMaterial, searchMaterials]);

  // Load materials when modal opens
  useEffect(() => {
    if (showAddMaterial) {
      searchMaterials('');
    }
  }, [showAddMaterial, searchMaterials]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleAddMaterial = async (material) => {
    setAddingMaterial(true);
    try {
      const { error: addError } = await addMaterialToProject(projectId, {
        material_id: material.id,
        material_name: material.name,
        material_image: material.image_url,
        material_type: material.category
      });

      if (addError) throw addError;

      toast.success(`${material.name} aggiunto al progetto`);
      setShowAddMaterial(false);
      fetchProject(); // Refresh
    } catch (err) {
      toast.error(err.message || 'Errore nell\'aggiunta del materiale');
    } finally {
      setAddingMaterial(false);
    }
  };

  const handleRemoveMaterial = async (projectMaterialId) => {
    try {
      const { error: removeError } = await removeMaterialFromProject(projectMaterialId);
      if (removeError) throw removeError;

      toast.success('Materiale rimosso');
      setShowDeleteConfirm(null);
      fetchProject();
    } catch (err) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleSaveNotes = async () => {
    if (!showNotesModal) return;
    
    setSavingNotes(true);
    try {
      const { error: notesError } = await updateProjectMaterialNotes(showNotesModal.id, notesText);
      if (notesError) throw notesError;

      toast.success('Note salvate');
      setShowNotesModal(null);
      fetchProject();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    if (!editForm.name.trim()) {
      toast.error('Inserisci un nome per il progetto');
      return;
    }

    setSaving(true);
    try {
      const { error: updateError } = await updateProject(projectId, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        client: editForm.client.trim() || null,
        cover_image: editForm.cover_image.trim() || null
      });

      if (updateError) throw updateError;

      toast.success('Progetto aggiornato');
      setShowEditProject(false);
      fetchProject();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  // Handle drag & drop reorder
  const handleReorder = async (newOrderedMaterials) => {
    // Update local state immediately for responsive UI
    const updatedMaterials = newOrderedMaterials.map((m, index) => ({
      ...m,
      sort_order: index
    }));
    setMaterials(updatedMaterials);

    // Persist to database
    try {
      const orderedIds = newOrderedMaterials.map(m => m.id);
      const { error } = await reorderProjectMaterials(projectId, orderedIds);
      
      if (error) {
        // Revert on error
        fetchProject();
        toast.error('Errore nel riordinamento');
      } else {
        toast.success('Ordine salvato');
      }
    } catch (err) {
      fetchProject();
      toast.error('Errore nel riordinamento');
    }
  };

  // Open notes modal
  const handleOpenNotes = (pm) => {
    setNotesText(pm.notes || '');
    setShowNotesModal(pm);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#b08b5c] mx-auto mb-4" />
          <p className="text-white/60">Caricamento progetto...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Box className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-light text-white mb-2">Progetto non trovato</h2>
          <p className="text-white/50 text-sm mb-6">{error || 'Il progetto richiesto non esiste o non hai accesso.'}</p>
          <Button
            onClick={() => navigate('/projects-new')}
            className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
          >
            Torna ai Progetti
          </Button>
        </div>
      </div>
    );
  }

  // Get cover image
  const coverImage = project.cover_image || 
    materials[0]?.material_image ||
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80';

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Progetti', href: '/projects-new' },
            { label: project.name }
          ]}
        />

        {/* Back Button */}
        <button
          onClick={() => navigate('/projects-new')}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
          data-testid="back-to-projects"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Torna ai Progetti</span>
        </button>

        {/* Project Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Cover Image */}
          <div className="lg:col-span-1">
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-white/5">
              <img
                src={coverImage}
                alt={project.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Project Info */}
          <div className="lg:col-span-2 flex flex-col justify-center">
            <div className="flex items-start justify-between mb-4">
              <div>
                {project.is_demo && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#b08b5c]/20 border border-[#b08b5c]/30 text-[#b08b5c] text-xs font-medium mb-3">
                    <Sparkles className="w-3 h-3" />
                    <span>Demo</span>
                  </div>
                )}
                <h1 className="text-3xl md:text-4xl font-light text-white mb-2" data-testid="project-title">
                  {project.name}
                </h1>
                {project.client && (
                  <p className="text-white/50">{project.client}</p>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEditProject(true)}
                className="text-white/40 hover:text-white hover:bg-white/10"
                data-testid="edit-project-btn"
              >
                <Edit3 className="w-5 h-5" />
              </Button>
            </div>

            {project.description && (
              <p className="text-white/60 mb-6 leading-relaxed">{project.description}</p>
            )}

            <div className="flex items-center gap-6 text-sm text-white/40">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Aggiornato: {new Date(project.updated_at).toLocaleDateString('it-IT')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Box className="w-4 h-4" />
                <span>{materials.length} materiali</span>
              </div>
            </div>
          </div>
        </div>

        {/* Materials Section */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-light text-white">Materiali del Progetto</h2>
              {materials.length > 1 && (
                <p className="text-white/40 text-xs mt-1 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" />
                  Trascina per riordinare
                </p>
              )}
            </div>
            <Button
              onClick={() => setShowAddMaterial(true)}
              className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
              data-testid="add-material-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Materiale
            </Button>
          </div>

          {/* Materials Grid with Drag & Drop */}
          {materials.length > 0 ? (
            <SortableMaterialsGrid
              materials={materials}
              onReorder={handleReorder}
              onOpenNotes={handleOpenNotes}
              onDelete={(pm) => setShowDeleteConfirm(pm)}
            />
          ) : (
            /* Empty State */
            <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
              <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                <Box className="w-8 h-8 text-white/20" />
              </div>
              <h3 className="text-lg font-light text-white mb-2">Nessun materiale</h3>
              <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
                Inizia ad aggiungere materiali al tuo progetto per creare la tua selezione.
              </p>
              <Button
                onClick={() => setShowAddMaterial(true)}
                className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il primo materiale
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* ============================================================ */}
      {/* ADD MATERIAL MODAL */}
      {/* ============================================================ */}
      <Dialog open={showAddMaterial} onOpenChange={setShowAddMaterial}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-light">Aggiungi Materiale</DialogTitle>
            <DialogDescription className="text-white/50">
              Cerca e seleziona un materiale da aggiungere al progetto.
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative py-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca materiali..."
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="material-search-input"
            />
          </div>

          {/* Materials List */}
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            {searchLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#b08b5c]" />
              </div>
            ) : availableMaterials.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {availableMaterials.map((material) => (
                  <button
                    key={material.id}
                    onClick={() => handleAddMaterial(material)}
                    disabled={addingMaterial}
                    className="group relative text-left bg-white/5 rounded-lg overflow-hidden border border-white/10 hover:border-[#b08b5c]/50 transition-all disabled:opacity-50"
                    data-testid={`add-material-${material.id}`}
                  >
                    <div className="aspect-[3/2] overflow-hidden">
                      <img
                        src={material.image_url || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80'}
                        alt={material.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-white font-medium text-sm truncate">{material.name}</h4>
                      <p className="text-white/40 text-xs">{material.category}</p>
                    </div>
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-[#b08b5c]/0 group-hover:bg-[#b08b5c]/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-[#b08b5c] text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1">
                        <Plus className="w-3 h-3" />
                        Aggiungi
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-white/40">
                <p>Nessun materiale trovato</p>
                <p className="text-xs mt-1">Prova con una ricerca diversa</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* EDIT PROJECT MODAL */}
      {/* ============================================================ */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-light">Modifica Progetto</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleUpdateProject} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-white/70">Nome del progetto *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                required
                data-testid="edit-project-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-client" className="text-white/70">Cliente / Riferimento</Label>
              <Input
                id="edit-client"
                value={editForm.client}
                onChange={(e) => setEditForm({ ...editForm, client: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="edit-project-client"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-desc" className="text-white/70">Descrizione</Label>
              <Textarea
                id="edit-desc"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                data-testid="edit-project-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-cover" className="text-white/70">Immagine di copertina (URL)</Label>
              <Input
                id="edit-cover"
                value={editForm.cover_image}
                onChange={(e) => setEditForm({ ...editForm, cover_image: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="https://..."
                data-testid="edit-project-cover"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowEditProject(false)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
                data-testid="save-project-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* NOTES MODAL */}
      {/* ============================================================ */}
      <Dialog open={!!showNotesModal} onOpenChange={() => setShowNotesModal(null)}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-light">
              Note per {showNotesModal?.material_name}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Aggiungi note per questo materiale..."
              className="bg-white/5 border-white/10 text-white min-h-[120px] placeholder:text-white/30"
              data-testid="material-notes-input"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowNotesModal(null)}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
              data-testid="save-notes-btn"
            >
              {savingNotes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salva Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* DELETE CONFIRM DIALOG */}
      {/* ============================================================ */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <AlertDialogContent className="bg-[#0a0a0f] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Rimuovere questo materiale?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              {showDeleteConfirm?.material_name} verrà rimosso dal progetto. 
              Il materiale resterà comunque disponibile nella libreria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showDeleteConfirm && handleRemoveMaterial(showDeleteConfirm.id)}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-remove-material"
            >
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectDetailPage;
